import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import UnifiedPortalHeader from "@/components/UnifiedPortalHeader";
import SharedFooter from "@/components/SharedFooter";
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  CheckCircle2,
  Clock,
  Lock,
  Plus,
  Target,
  TrendingUp,
  Trophy,
  Sparkles,
  CalendarDays,
  AlertTriangle,
  Lightbulb,
  User,
  History,
  Trash2,
} from "lucide-react";

export default function StudyPlans() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [planType, setPlanType] = useState("");
  const [currentPlan, setCurrentPlan] = useState(null);
  const [planHistory, setPlanHistory] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [view, setView] = useState("create"); // create, view, history
  const [planFilter, setPlanFilter] = useState("all"); // all, active, completed
  const [planConfig, setPlanConfig] = useState({
    subjects: [],
    duration_weeks: 4,
    daily_hours: 1.0,
    focus_areas: [],
    exam_date: "",
  });
  const navigate = useNavigate();

  const fetchProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        // Pre-fill subjects from subscription
        const subjects = data.user?.subscription?.subjects || ["Mathematics", "Science"];
        setPlanConfig(prev => ({ ...prev, subjects }));
      } else if (response.status === 401) {
        navigate("/login");
      }
    } catch (error) {
      toast.error("Failed to load profile");
    }
  }, [navigate]);

  const checkAccess = useCallback(async () => {
    try {
      const response = await fetch(`${API}/study-plans/available`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setHasAccess(data.has_access);
        setPlanType(data.plan_type);
        setUpgradeMessage(data.upgrade_message);
      }
    } catch (error) {
      console.error("Access check failed:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCurrentPlan = useCallback(async () => {
    try {
      const response = await fetch(`${API}/study-plans/current`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.has_plan) {
          setCurrentPlan(data.plan);
        }
      }
    } catch (error) {
      console.error("Failed to fetch current plan:", error);
    }
  }, []);

  const fetchPlanHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API}/study-plans/history?limit=50`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        console.log("Plan history response:", data);
        setPlanHistory(data.plans || []);
      }
    } catch (error) {
      console.error("Failed to fetch history:", error);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    checkAccess();
  }, [fetchProfile, checkAccess]);

  useEffect(() => {
    if (hasAccess) {
      fetchCurrentPlan();
      fetchPlanHistory();
    }
  }, [hasAccess, fetchCurrentPlan, fetchPlanHistory]);

  const generatePlan = async () => {
    if (planConfig.subjects.length === 0) {
      toast.error("Please select at least one subject");
      return;
    }

    setGenerating(true);
    try {
      const response = await fetch(`${API}/study-plans/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(planConfig),
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentPlan({ ...data, plan_content: data.plan_content });
        setView("view");
        toast.success("Study plan generated!");
        fetchPlanHistory();
      } else {
        const error = await response.json();
        if (error.detail?.error === "feature_locked") {
          toast.error(error.detail.message);
        } else {
          toast.error("Failed to generate plan");
        }
      }
    } catch (error) {
      toast.error("Failed to generate plan");
    } finally {
      setGenerating(false);
    }
  };

  const markDayComplete = async () => {
    if (!currentPlan) return;

    try {
      const response = await fetch(`${API}/study-plans/${currentPlan.id || currentPlan.plan_id}/complete-day`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentPlan(prev => ({
          ...prev,
          progress: {
            ...prev.progress,
            days_completed: data.days_completed,
            weeks_completed: data.weeks_completed,
          },
          status: data.status,
        }));
        toast.success("Day marked as complete!");
      }
    } catch (error) {
      toast.error("Failed to update progress");
    }
  };

  const toggleSubject = (subject) => {
    setPlanConfig(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject],
    }));
  };

  // Calculate unique plans count (deduplicated)
  const getUniquePlansCount = useCallback(() => {
    const planIds = new Set();
    
    planHistory.forEach(p => {
      const planId = p.id || p.plan_id;
      if (planId) planIds.add(planId);
    });
    
    if (currentPlan) {
      const currentId = currentPlan.id || currentPlan.plan_id;
      if (currentId) planIds.add(currentId);
    }
    
    return planIds.size;
  }, [planHistory, currentPlan]);

  const uniquePlansCount = getUniquePlansCount();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Feature locked view
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <UnifiedPortalHeader
          portalType="student"
          userName=""
          userSubtitle=""
          onLogout={() => navigate("/student/profile")}
          showUserInfo={false}
          showHomeButton={false}
          hideLogout={true}
        />

        <main className="max-w-4xl mx-auto px-4 py-8 flex-1">
          <Card className="border-0 shadow-lg">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-violet-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Pro Feature
              </h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                {upgradeMessage || "AI Study Plans are available exclusively on the Pro plan. Get personalized study schedules based on your performance!"}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  className="bg-gradient-to-r from-violet-600 to-purple-600"
                  onClick={() => navigate("/parent/dashboard")}
                  data-testid="upgrade-btn"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Upgrade to Pro
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/student/profile")}
                >
                  <User className="w-4 h-4 mr-2" />
                  Back to Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>

        <SharedFooter />
      </div>
    );
  }

  // Create plan view
  if (view === "create") {
    const allSubjects = user?.subscription?.subjects || ["Mathematics", "Science", "English Grammar", "Hindi Vyakaran"];
    
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <UnifiedPortalHeader
          portalType="student"
          userName=""
          userSubtitle=""
          onLogout={() => navigate("/student/profile")}
          showUserInfo={false}
          showHomeButton={false}
          hideLogout={true}
        />

        <main className="max-w-2xl mx-auto px-4 py-6 space-y-6 flex-1">
          {/* Parent Guidance Card */}
          <Card className="border-0 shadow-md bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-l-amber-400">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Tips for Parents</h3>
                  <ul className="text-sm text-gray-600 space-y-1.5">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">•</span>
                      <span><strong>Focus on weak areas:</strong> Select subjects where your child needs more practice</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">•</span>
                      <span><strong>Be realistic with time:</strong> Start with 30-60 mins daily, adjust based on attention span</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">•</span>
                      <span><strong>Set exam dates:</strong> If exams are coming up, add the date for deadline-focused planning</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">•</span>
                      <span><strong>Consistency matters:</strong> Shorter daily sessions beat long weekend cramming</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-violet-600" />
                Select Subjects
              </CardTitle>
              <CardDescription>Choose subjects to include in your study plan</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {allSubjects.map((subject) => (
                <Button
                  key={subject}
                  variant={planConfig.subjects.includes(subject) ? "default" : "outline"}
                  size="sm"
                  className={planConfig.subjects.includes(subject) ? "bg-violet-600" : ""}
                  onClick={() => toggleSubject(subject)}
                >
                  {subject}
                </Button>
              ))}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-violet-600" />
                Plan Duration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Duration (weeks)</label>
                <div className="flex gap-2 mt-2">
                  {[2, 4, 6, 8].map((weeks) => (
                    <Button
                      key={weeks}
                      variant={planConfig.duration_weeks === weeks ? "default" : "outline"}
                      size="sm"
                      className={planConfig.duration_weeks === weeks ? "bg-violet-600" : ""}
                      onClick={() => setPlanConfig({ ...planConfig, duration_weeks: weeks })}
                    >
                      {weeks} weeks
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Daily Study Time (hours)</label>
                <div className="flex gap-2 mt-2">
                  {[0.5, 1, 1.5, 2].map((hours) => (
                    <Button
                      key={hours}
                      variant={planConfig.daily_hours === hours ? "default" : "outline"}
                      size="sm"
                      className={planConfig.daily_hours === hours ? "bg-violet-600" : ""}
                      onClick={() => setPlanConfig({ ...planConfig, daily_hours: hours })}
                    >
                      {hours}h
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Exam Date (optional)</label>
                <input
                  type="date"
                  className="w-full mt-2 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  value={planConfig.exam_date}
                  onChange={(e) => setPlanConfig({ ...planConfig, exam_date: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-3">
            <Button
              className="w-full h-14 bg-gradient-to-r from-violet-600 to-purple-600 text-lg"
              onClick={generatePlan}
              disabled={generating || planConfig.subjects.length === 0}
              data-testid="generate-plan-btn"
            >
              {generating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Generating Plan...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate AI Study Plan
                </>
              )}
            </Button>
            
            {/* Show link to existing plans if there are any */}
            {uniquePlansCount > 0 && (
              <Button
                className="w-full bg-violet-100 text-violet-700 hover:bg-violet-200 border border-violet-200"
                onClick={() => setView("history")}
              >
                <History className="w-4 h-4 mr-2" />
                View Existing Plans ({uniquePlansCount})
              </Button>
            )}
            
            <Button
              className="w-full bg-gray-200 text-gray-700 hover:bg-gray-300"
              onClick={() => navigate("/student/profile")}
            >
              <User className="w-4 h-4 mr-2" />
              Back to Profile
            </Button>
          </div>
        </main>

        <SharedFooter />
      </div>
    );
  }

  // View plan
  if (view === "view" && currentPlan) {
    const progress = currentPlan.progress || { days_completed: 0, total_days: 28 };
    const progressPercent = Math.round((progress.days_completed / progress.total_days) * 100);

    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <UnifiedPortalHeader
          portalType="student"
          userName=""
          userSubtitle=""
          onLogout={() => navigate("/student/profile")}
          showUserInfo={false}
          showHomeButton={false}
          hideLogout={true}
        />

        <main className="max-w-4xl mx-auto px-4 py-6 space-y-6 flex-1">
          {/* Progress Card */}
          <Card className="bg-gradient-to-r from-violet-600 to-purple-600 text-white border-0 shadow-lg">
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold">Study Progress</h3>
                    <Badge className={`${currentPlan.status === "completed" ? "bg-green-400 text-green-900" : "bg-white/20 text-white border-0"}`}>
                      {currentPlan.status === "completed" ? "Completed" : "Active"}
                    </Badge>
                  </div>
                  <p className="text-violet-200">
                    Day {progress.days_completed} of {progress.total_days}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold">{progressPercent}%</p>
                  <p className="text-sm text-violet-200">Complete</p>
                </div>
              </div>
              <Progress value={progressPercent} className="h-3 bg-violet-400" />
              <div className="flex flex-wrap gap-3 mt-4">
                {currentPlan.status !== "completed" && (
                  <Button
                    className="bg-white text-violet-600 hover:bg-violet-50"
                    onClick={markDayComplete}
                    data-testid="mark-complete-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Mark Today Complete
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="bg-white/10 text-white border-white/30 hover:bg-white/20"
                  onClick={() => setView("history")}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Plans
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Weak Topics */}
          {currentPlan.weak_topics && currentPlan.weak_topics.length > 0 && (
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Focus Areas
                </CardTitle>
                <CardDescription>Topics that need extra attention based on your performance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {currentPlan.weak_topics.map((topic, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{topic.topic}</p>
                        <p className="text-sm text-gray-500">{topic.subject}</p>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700">
                        {topic.accuracy}% accuracy
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Plan Content */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-violet-600" />
                Study Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none">
                <ReactMarkdown>{currentPlan.plan_content}</ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </main>

        <SharedFooter />
      </div>
    );
  }

  // History view - shows existing plans
  const viewPlanDetails = async (plan) => {
    // If plan doesn't have plan_content, fetch the full plan
    if (!plan.plan_content) {
      try {
        const response = await fetch(`${API}/study-plans/${plan.id || plan.plan_id}`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          if (data.plan) {
            setCurrentPlan(data.plan);
            setView("view");
            return;
          }
        }
      } catch (error) {
        console.error("Failed to fetch plan details:", error);
      }
    }
    setCurrentPlan(plan);
    setView("view");
  };

  const deletePlan = async (e, plan) => {
    e.stopPropagation(); // Prevent triggering the plan click
    
    const planId = plan.id || plan.plan_id;
    if (!planId) return;
    
    if (!window.confirm("Are you sure you want to delete this study plan? This action cannot be undone.")) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/study-plans/${planId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Plan deleted successfully");
        // Remove from local state
        setPlanHistory(prev => prev.filter(p => (p.id || p.plan_id) !== planId));
        if (currentPlan && (currentPlan.id === planId || currentPlan.plan_id === planId)) {
          setCurrentPlan(null);
        }
      } else {
        toast.error("Failed to delete plan");
      }
    } catch (error) {
      console.error("Failed to delete plan:", error);
      toast.error("Failed to delete plan");
    }
  };

  // Combine current plan with history for display - avoid duplicates
  const allPlans = (() => {
    const planMap = new Map();
    
    // Add history plans first
    planHistory.forEach(p => {
      const planId = p.id || p.plan_id;
      if (planId) {
        planMap.set(planId, p);
      }
    });
    
    // Add/update with current plan (has more complete data)
    if (currentPlan) {
      const currentId = currentPlan.id || currentPlan.plan_id;
      if (currentId) {
        planMap.set(currentId, currentPlan);
      }
    }
    
    // Convert to array and sort by created_at (newest first)
    const result = Array.from(planMap.values()).sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
    
    return result;
  })();

  // Filter plans based on selected filter
  const filteredPlans = allPlans.filter(plan => {
    if (planFilter === "all") return true;
    if (planFilter === "active") return plan.status === "active";
    if (planFilter === "completed") return plan.status === "completed";
    return true;
  });

  // Count by status
  const activePlansCount = allPlans.filter(p => p.status === "active").length;
  const completedPlansCount = allPlans.filter(p => p.status === "completed").length;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <UnifiedPortalHeader
        portalType="student"
        userName=""
        userSubtitle=""
        onLogout={() => navigate("/student/profile")}
        showUserInfo={false}
        showHomeButton={false}
        hideLogout={true}
      />

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6 flex-1">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Your Study Plans</h1>
          <p className="text-gray-600">View and manage your AI-generated study plans</p>
        </div>

        {/* No Plans State */}
        {allPlans.length === 0 && (
          <Card className="border-0 shadow-md">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-violet-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Study Plans Yet</h3>
              <p className="text-gray-600 mb-4">Create your first AI-powered study plan to get started!</p>
              <Button
                className="bg-gradient-to-r from-violet-600 to-purple-600"
                onClick={() => setView("create")}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Create Your First Plan
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Plan History Management */}
        {allPlans.length > 0 && (
          <>
            {/* Filter Tabs */}
            <div className="flex gap-2">
              <Button
                variant={planFilter === "all" ? "default" : "outline"}
                size="sm"
                className={planFilter === "all" ? "bg-violet-600" : ""}
                onClick={() => setPlanFilter("all")}
              >
                All Plans ({allPlans.length})
              </Button>
              <Button
                variant={planFilter === "active" ? "default" : "outline"}
                size="sm"
                className={planFilter === "active" ? "bg-emerald-600" : ""}
                onClick={() => setPlanFilter("active")}
              >
                <TrendingUp className="w-4 h-4 mr-1" />
                Active ({activePlansCount})
              </Button>
              <Button
                variant={planFilter === "completed" ? "default" : "outline"}
                size="sm"
                className={planFilter === "completed" ? "bg-green-600" : ""}
                onClick={() => setPlanFilter("completed")}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Completed ({completedPlansCount})
              </Button>
            </div>

            {/* Plans List */}
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <History className="w-5 h-5 text-violet-600" />
                  {planFilter === "all" ? "All Plans" : planFilter === "active" ? "Active Plans" : "Completed Plans"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {filteredPlans.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No {planFilter} plans found.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredPlans.map((plan, index) => {
                      const progressPercent = plan.progress 
                        ? Math.round((plan.progress.days_completed / plan.progress.total_days) * 100) 
                        : 0;
                      
                      return (
                        <div
                          key={plan.id || plan.plan_id || index}
                          className={`p-4 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                            plan.status === "active" 
                              ? "bg-gradient-to-r from-violet-50 to-purple-50 border-2 border-violet-200 hover:border-violet-400" 
                              : "bg-gray-50 hover:bg-gray-100 border border-gray-200"
                          }`}
                          onClick={() => viewPlanDetails(plan)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                plan.status === "active" ? "bg-violet-100" : "bg-green-100"
                              }`}>
                                {plan.status === "completed" ? (
                                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                                ) : (
                                  <TrendingUp className="w-6 h-6 text-violet-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {plan.duration_weeks}-Week Study Plan
                                </p>
                                <p className="text-sm text-gray-500">
                                  {plan.subjects?.join(", ") || "Multiple subjects"}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className={
                                plan.status === "active" 
                                  ? "bg-violet-100 text-violet-700" 
                                  : "bg-green-100 text-green-700"
                              }>
                                {plan.status === "active" ? "Active" : "Completed"}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2"
                                onClick={(e) => deletePlan(e, plan)}
                                title="Delete plan"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Progress Bar for Active Plans */}
                          {plan.status === "active" && plan.progress && (
                            <div className="mt-3 pt-3 border-t border-violet-200">
                              <div className="flex items-center justify-between text-sm mb-2">
                                <span className="text-gray-600">Progress</span>
                                <span className="font-medium text-violet-600">
                                  Day {plan.progress.days_completed} of {plan.progress.total_days} ({progressPercent}%)
                                </span>
                              </div>
                              <Progress value={progressPercent} className="h-2" />
                            </div>
                          )}
                          
                          {/* Completion Info for Completed Plans */}
                          {plan.status === "completed" && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex items-center gap-2 text-sm text-green-600">
                                <CheckCircle2 className="w-4 h-4" />
                                <span>Completed all {plan.progress?.total_days || plan.duration_weeks * 7} days</span>
                              </div>
                            </div>
                          )}
                          
                          {/* Meta Info */}
                          <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                            <span>Created {new Date(plan.created_at).toLocaleDateString()}</span>
                            {plan.daily_hours && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {plan.daily_hours}h/day
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Navigation Buttons - Centered */}
        <div className="flex justify-center gap-3 pt-4">
          <Button
            variant="outline"
            className="border-violet-300 text-violet-700 hover:bg-violet-50"
            onClick={() => setView("create")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Study Plan
          </Button>
          <Button
            className="bg-gradient-to-r from-violet-600 to-purple-600 text-white"
            onClick={() => navigate("/student/profile")}
          >
            <User className="w-4 h-4 mr-2" />
            Back to Profile
          </Button>
        </div>
      </main>

      <SharedFooter />
    </div>
  );
}
